import { Filter as FilterIcon, GripVertical, Pencil, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Filter } from '@vivport/backend/types';
import type { ContentType } from '@vivport/backend/types';
import { ProvenanceAttribution } from './ProvenanceAttribution';

const contentTypeLabels: Record<ContentType, string> = {
  ANCHOR: 'ANCHOR',
  EVENT: 'EVENT',
  MESSAGE: 'Message',
  PHOTO: 'Photo',
  VIDEO: 'Video',
  RECORDING: 'Recording',
  DOCUMENT: 'DOCUMENT',
  FILE: 'File',
  URL: 'URL',
  NEO_CHAT: 'Neo Chat',
  PROCESS_RUN: 'Process Run',
  MANUAL_SESSION: 'Manual Session',
  MESSAGE_EXTRACT: 'Extract',
  COLLECTION: 'Collection',
  TEMPLATE: 'Template',
};

function formatContentTypes(filter: Filter): string {
  const contentTypes = filter.definition.contentTypes;
  if (!contentTypes || contentTypes.length === 0) {
    return 'All content types';
  }
  return contentTypes.map((ct) => contentTypeLabels[ct]).join(', ');
}

interface FilterListItemProps {
  filter: Filter;
  canEdit: boolean;
  canDelete: boolean;
  isDraggable: boolean;
  onEdit: (filter: Filter) => void;
  onDelete: (filter: Filter) => void;
  sortableId?: string;
}

export function FilterListItem({
  filter,
  canEdit,
  canDelete,
  isDraggable,
  onEdit,
  onDelete,
  sortableId,
}: FilterListItemProps) {
  const definition = filter.definition as { tabLabel?: string } | undefined;
  const displayLabel = definition?.tabLabel ?? filter.name;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId ?? `filter-${filter.id}`,
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 px-4 py-2 transition-colors ${
        isDragging ? 'opacity-50 bg-muted/60' : 'hover:bg-muted/40'
      }`}
    >
      {isDraggable ? (
        <button
          type="button"
          className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 -ml-0.5 text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      ) : (
        <div className="w-5 flex-shrink-0" aria-hidden />
      )}
      <div className="w-4 h-4 shrink-0 text-muted-foreground">
        {filter.isTab ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
            />
          </svg>
        ) : (
          <FilterIcon className="h-3.5 w-3.5" />
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
        <span className="font-medium text-sm truncate">{displayLabel}</span>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="h-5 px-1.5 text-[10px] leading-none">{filter.ownerScope}</Badge>
          <span className="text-[11px] text-muted-foreground truncate">
            {formatContentTypes(filter)}
          </span>
          <ProvenanceAttribution lastChange={filter.lastChange} fallbackDate={filter.updatedAt} />
        </div>
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onEdit(filter)}
            aria-label="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onDelete(filter)}
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </li>
  );
}

